/**
 * READ-LAYER INHERITANCE of a company's shared contact methods.
 *
 * A genuinely shared method — a front desk, a switchboard, `info@` — belongs to
 * the COMPANY, never to a person there (see CONTACT_METHOD_EXCLUSIVITY). The
 * 2026-08-21 remediation acted on that: it moved shared numbers off people and
 * onto their companies, which left roughly 745 contact rows in production
 * showing no phone number at all — the number was still reachable, just owned
 * one level up with nothing in the UI reading it.
 *
 * This module is that reader. It PROJECTS the company's shared methods onto its
 * people so a rep can still call the office from the person's card, while
 * keeping the two kinds of method structurally distinct: an inherited method
 * carries `scope: "company"` and is badged "Company line", so nobody mistakes a
 * switchboard for someone's direct line.
 *
 * It is a projection and nothing else:
 *
 *   - NOTHING here writes. Copying an inherited value back onto the contact row
 *     would re-create the exact collision the single-owner constraint forbids
 *     (`contact_method_claims` + `trg_contact_method_claims_single_owner`), and
 *     re-manufacture the duplicate "people" the model exists to prevent.
 *   - An inherited method is never editable. Surfaces must not give it a
 *     `fieldId` or an inline editor — the shared method is edited on the
 *     COMPANY page (`organization_phone` / `organization_email` and the alias
 *     array editors).
 *   - An anchor inherits nothing. `contacts.is_company_line = true` rows are not
 *     people; they hold no methods of their own and exist only because
 *     `communications.contact_id` is NOT NULL.
 *
 * Pure and client-safe on purpose — the same resolution has to run in Hub's
 * detail header, its Details tab, its detail modal info panel, and every native
 * client that renders a contact, and all of them must agree on what is
 * inherited. Keys come from `@west-tennessee-consulting/contact-identity`, the
 * TypeScript mirror of the SQL key functions, so "the person already has this
 * number" means the same thing here as it does in Postgres.
 *
 * The row types below are STRUCTURAL, naming the columns only. An app with
 * generated schema types should bind them to its own (Hub does:
 * `Partial<Pick<Contacts, …>>`), which is what makes a renamed or dropped
 * column fail to compile at that binding instead of silently resolving nothing
 * at runtime.
 */

import {
  emailMethodKey,
  phoneMethodKey,
} from "@west-tennessee-consulting/contact-identity/method-keys";

/** The one badge string every inherited-method surface renders. */
export const COMPANY_LINE_BADGE_LABEL = "Company line";

/** Which company column an inherited method came from. */
export type InheritedMethodSource =
  | "organization_phone"
  | "phone_aliases"
  | "organization_email"
  | "email_aliases";

/**
 * A contact method the person does NOT own.
 *
 * `scope: "company"` is a required literal rather than an optional flag so an
 * inherited method can never be passed where a personal one is expected without
 * the type system noticing — the API-shape half of "visibly and structurally
 * distinguishable".
 */
export interface InheritedContactMethod {
  readonly scope: "company";
  readonly kind: "phone" | "email";
  /** The shared value, trimmed. Display and dial/compose use this as-is. */
  readonly value: string;
  readonly companyId: string | null;
  readonly companyName: string | null;
  /**
   * The company's line-contact anchor, when it has one. Activity on a shared
   * line belongs to the anchor, not to the colleague whose card it was launched
   * from — see `inheritedMethodEntityContext`.
   */
  readonly lineContactId: string | null;
  readonly source: InheritedMethodSource;
}

/** The person's own methods. Every field optional: surfaces load different subsets. */
export interface ContactOwnMethods {
  phone?: string | null;
  phone_aliases?: readonly (string | null)[] | null;
  email?: string | null;
  email_aliases?: readonly (string | null)[] | null;
  is_company_line?: boolean | null;
}

/** The company's shared methods, and the anchor they hang off. */
export interface CompanySharedMethods {
  id?: string | null;
  name?: string | null;
  organization_phone?: string | null;
  phone_aliases?: readonly (string | null)[] | null;
  organization_email?: string | null;
  email_aliases?: readonly (string | null)[] | null;
  line_contact_id?: string | null;
}

export interface InheritedContactMethods {
  /** Ordered: `organization_phone` first, then `phone_aliases` as stored. */
  readonly phones: readonly InheritedContactMethod[];
  readonly emails: readonly InheritedContactMethod[];
}

const NONE: InheritedContactMethods = { phones: [], emails: [] };

function cleanValues(
  primary: string | null | undefined,
  aliases: readonly (string | null)[] | null | undefined
): { value: string; fromPrimary: boolean }[] {
  const out: { value: string; fromPrimary: boolean }[] = [];
  const primaryTrimmed = primary?.trim();
  if (primaryTrimmed) out.push({ value: primaryTrimmed, fromPrimary: true });
  for (const alias of aliases ?? []) {
    const trimmed = alias?.trim();
    if (trimmed) out.push({ value: trimmed, fromPrimary: false });
  }
  return out;
}

function resolveKind(
  kind: "phone" | "email",
  own: { value: string }[],
  shared: { value: string; fromPrimary: boolean }[],
  company: CompanySharedMethods,
  key: (raw: string) => string | null
): InheritedContactMethod[] {
  // The person owns a method of this kind, so the company's shared one is not
  // how you reach THEM — their direct line wins and nothing is inherited.
  //
  // This also settles the legacy-conflict case for free: where a pre-remediation
  // duplicate still leaves the same number on both owners, the person's copy is
  // the one that renders, as theirs, exactly once. No dedupe pass is needed
  // because a person with any method of this kind inherits none.
  if (own.length > 0) return [];

  const seen = new Set<string>();
  const inherited: InheritedContactMethod[] = [];

  for (const { value, fromPrimary } of shared) {
    const k = key(value);
    // An unkeyable value (garbage, a too-short number) is still worth showing —
    // it is what a human would dial — but it cannot be deduped, so key on the
    // literal text instead.
    const dedupeKey = k ?? `raw:${value.toLowerCase()}`;
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);

    inherited.push({
      scope: "company",
      kind,
      value,
      companyId: company.id ?? null,
      companyName: company.name ?? null,
      lineContactId: company.line_contact_id ?? null,
      source: fromPrimary
        ? kind === "phone"
          ? "organization_phone"
          : "organization_email"
        : kind === "phone"
          ? "phone_aliases"
          : "email_aliases",
    });
  }

  return inherited;
}

/**
 * Which of the company's shared methods this person inherits.
 *
 * Returns nothing at all when the contact is a company-line anchor, or when
 * there is no company. A kind is inherited only when the person owns no method
 * of that kind — their own direct line always wins, and is never shown as
 * inherited.
 */
export function resolveInheritedContactMethods(
  contact: ContactOwnMethods | null | undefined,
  company: CompanySharedMethods | null | undefined
): InheritedContactMethods {
  if (!contact || !company) return NONE;
  if (contact.is_company_line === true) return NONE;

  const ownPhones = cleanValues(contact.phone, contact.phone_aliases);
  const ownEmails = cleanValues(contact.email, contact.email_aliases);
  const sharedPhones = cleanValues(company.organization_phone, company.phone_aliases);
  const sharedEmails = cleanValues(company.organization_email, company.email_aliases);

  const phones = resolveKind("phone", ownPhones, sharedPhones, company, phoneMethodKey);
  const emails = resolveKind("email", ownEmails, sharedEmails, company, emailMethodKey);

  if (phones.length === 0 && emails.length === 0) return NONE;
  return { phones, emails };
}

/** Human-readable provenance for a badge tooltip / `title`. */
export function inheritedMethodTooltip(method: InheritedContactMethod): string {
  const owner = method.companyName?.trim() || "this company";
  const what = method.kind === "phone" ? "line" : "mailbox";
  return `Shared ${what} owned by ${owner} — not this person's direct ${
    method.kind === "phone" ? "number" : "address"
  }`;
}

/**
 * The entity context a compose / send-text modal should use for an INHERITED
 * method.
 *
 * A text to the front desk is the company's conversation, so it must be logged
 * against the company's line anchor — logging it on whichever colleague's card
 * the rep happened to be looking at is precisely the mis-attribution the anchor
 * exists to prevent. Returns `null` when the company has no anchor yet, and the
 * caller then keeps whatever context it already had: today's behaviour, no
 * worse, until the anchor is provisioned.
 */
export function inheritedMethodEntityContext(method: InheritedContactMethod): {
  item: Record<string, unknown>;
  entityType: string;
} | null {
  if (!method.lineContactId) return null;
  return {
    entityType: "contacts",
    item: {
      id: method.lineContactId,
      name: method.companyName
        ? `${method.companyName} — ${COMPANY_LINE_BADGE_LABEL}`
        : COMPANY_LINE_BADGE_LABEL,
      company_id: method.companyId ?? undefined,
      company_name: method.companyName ?? undefined,
    },
  };
}
